import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Compass,
  RotateCcw,
  ArrowRight,
  Zap,
  Layers,
  CloudFog,
  HeartCrack,
  HeartHandshake,
  ShieldAlert,
  LifeBuoy,
} from "lucide-react";
import { withMemberGate } from "@/lib/with-member-gate";

export const Route = createFileRoute("/regulation-compass")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "My Regulation Compass — Recovery Trauma™" },
      {
        name: "description",
        content:
          "An interactive compass for the question 'What do I need right now?'. Choose the state closest to your experience and receive gentle, trauma-informed options.",
      },
      { property: "og:title", content: "My Regulation Compass — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Choose what feels closest to your experience right now and get a few gentle options to help you regulate.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/regulation-compass" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/regulation-compass" },
    ],
  }),
  component: withMemberGate(RegulationCompassPage, "My Regulation Compass"),
});

// ────────────────────────────────────────────────────────────────────
// Data — self-regulation support only. Nothing here diagnoses a state
// or promises an outcome; every line is an invitation the member can
// decline.
// ────────────────────────────────────────────────────────────────────

type Cta = { label: string; to: string };

type CompassState = {
  id: string;
  /** Short label used inside the wheel segment — a single word. */
  short: string;
  /** Full label used in the details card. */
  title: string;
  icon: typeof Zap;
  /** Everyday-life example so the state is recognisable, in plain words. */
  example: string;
  /** How this state can show up — descriptive, never diagnostic. */
  feels: string[];
  /** A gentle, educational explanation of the nervous system's response. */
  why: string;
  /** What may help right now — invitations, never promises. */
  helps: string;
  /** Kind reminders the member can say to themselves. */
  reminders: string[];
  /** Small actions that may help. */
  actions: string[];
  ctas: Cta[];
  /** Accent hue token pair used for the segment + card. */
  tint: string;
  tintStrong: string;
};

const STATES: CompassState[] = [
  {
    id: "triggered",
    short: "Triggered",
    title: "I feel triggered",
    icon: Zap,
    tint: "var(--qa-peach-tint)",
    tintStrong: "var(--qa-peach-ink)",
    example:
      "Something happened that reminded me of a painful experience. My emotions suddenly feel much stronger, my body feels tense, or I feel like I need to react immediately.",
    feels: [
      "A feeling that arrives fast and out of proportion to the moment",
      "Heat, tension, a racing heart, or a jolt through the body",
      "An urge to react, defend, explain or leave straight away",
      "The present moment and an older memory blurring together",
    ],
    why: "When something in the present echoes something painful from the past, the nervous system can respond as though the older experience is happening again. That response is fast and automatic — it isn't a choice, and it isn't a sign that something is wrong with you.",
    helps:
      "Slowing things down before responding often helps more than trying to argue yourself out of the feeling. Bringing attention to the room you are actually in, and to a slower out-breath, can gently signal to your body that this is now, not then.",
    reminders: ["I am here, right now", "This feeling can pass"],
    actions: [
      "Look around and name 5 things you can see",
      "Feel both feet against the floor",
      "Slow your breathing",
    ],
    ctas: [{ label: "Try a Grounding Exercise", to: "/grounding" }],
  },
  {
    id: "overwhelmed",
    short: "Overwhelmed",
    title: "I feel overwhelmed",
    icon: Layers,
    tint: "var(--qa-sage-tint)",
    tintStrong: "var(--qa-sage-ink)",
    example:
      "Everything feels like too much. I can't think clearly, I don't know where to start, or even small things feel difficult right now.",
    feels: [
      "Thoughts moving too quickly to hold on to any of them",
      "Ordinary tasks feeling impossibly large",
      "Noise, light, messages or people feeling like too much input",
      "Tearfulness, irritability, or a wish to shut the door on everything",
    ],
    why: "When demands outpace the capacity available to you in this moment, the nervous system can move into a high-alert state where planning and decision-making become much harder. Capacity changes from day to day, and having less of it today says nothing about your ability.",
    helps:
      "Lowering the amount coming in tends to help before anything else — fewer inputs, one small step rather than the whole list. A longer out-breath can settle the body enough for thinking to return.",
    reminders: [
      "I don't have to solve everything right now",
      "One thing at a time",
    ],
    actions: [
      "Take a break",
      "Reduce stimulation around you",
      "Focus on one small next step",
    ],
    ctas: [{ label: "Open Breathing", to: "/breathing" }],
  },
  {
    id: "numb",
    short: "Numb",
    title: "I feel numb or disconnected",
    icon: CloudFog,
    tint: "var(--qa-indigo-tint)",
    tintStrong: "var(--qa-indigo-ink)",
    example:
      "I don't really feel anything. I feel far away from myself, emotionally switched off, spaced out, or disconnected from what is happening around me.",
    feels: [
      "Watching life from behind glass, or from slightly outside yourself",
      "Flatness where emotion would usually be",
      "Time blurring, or losing track of what you were doing",
      "The body feeling heavy, distant, or not quite yours",
    ],
    why: "When you feel numb or disconnected, your nervous system may be trying to reduce overwhelm by shutting down or creating distance from difficult emotions. It is a protective response, not a failure to care, and it often appears after a long stretch of coping.",
    helps:
      "You don't need to force yourself to feel differently. Gentle sensory grounding, small movements, familiar sounds, or noticing your surroundings may help you reconnect gradually, at whatever pace feels tolerable.",
    reminders: ["Notice the temperature around you"],
    actions: [
      "Notice where your body touches the chair or floor",
      "Hold something with texture",
      "Gently move your hands or feet",
      "Name where you are and what day it is",
    ],
    ctas: [{ label: "Try Somatic Regulation", to: "/somatic" }],
  },
  {
    id: "rejected",
    short: "Rejected",
    title: "I feel rejected or abandoned",
    icon: HeartCrack,
    tint: "var(--qa-rose-tint)",
    tintStrong: "var(--qa-rose-ink)",
    example:
      "Someone didn't reply, cancelled plans, pulled away, criticised me, or behaved differently, and I suddenly feel unwanted, unimportant, or afraid they will leave.",
    feels: [
      "A drop or ache in the chest or stomach after a small change in someone's tone",
      "Re-reading messages, looking for evidence of what has gone wrong",
      "An urge to chase reassurance — or to withdraw first",
      "Fear that arrives faster than the facts of the situation",
    ],
    why: "Human beings are wired for connection, and a threat to it registers in the body much like physical danger. If closeness was unpredictable earlier in life, the nervous system may have learned to detect distance very early and respond strongly.",
    helps:
      "Giving yourself the reassurance first, before acting on the fear, often creates enough space to see the situation more clearly. Writing down what you are afraid will happen can separate the old fear from the present moment.",
    reminders: [
      "This feeling is intense, but it doesn't define my worth",
      "I can give myself reassurance before reacting",
      "I don't need to chase reassurance immediately",
    ],
    actions: [
      "Reach out to someone safe if you need connection",
      "Write down what you are afraid will happen",
    ],
    ctas: [{ label: "Open Journal", to: "/journal" }],
  },
  {
    id: "ashamed",
    short: "Ashamed",
    title: "I feel ashamed or not good enough",
    icon: HeartHandshake,
    tint: "var(--qa-gold-tint)",
    tintStrong: "var(--qa-gold-ink)",
    example:
      "I feel like I've done something wrong, I'm judging myself harshly, comparing myself with others, or feeling that I'm not good enough.",
    feels: [
      "A harsh inner voice that sounds like certainty rather than opinion",
      "Wanting to hide, cancel plans, or disappear from view",
      "Heat in the face, a lowered head, a wish to be smaller",
      "Measuring yourself against other people and always coming up short",
    ],
    why: "Shame often grows where someone was blamed, criticised or held responsible for things outside their control. The nervous system can respond by making you want to withdraw — historically a way of staying safe within a group, even when it hurts now.",
    helps:
      "Shame tends to soften in the presence of warmth rather than argument. Speaking to yourself the way you would to someone you care about, and noticing whose voice the criticism resembles, can create a little distance from it.",
    reminders: [
      "Having a difficult moment doesn't make me a bad person",
      "My feelings deserve compassion",
      "I can make mistakes and still have worth",
    ],
    actions: [
      "Speak to yourself as you would to someone you care about",
      "Ask: \u201cWhose voice does this criticism sound like?\u201d",
    ],
    ctas: [{ label: "Explore Inner Child", to: "/inner-child" }],
  },
  {
    id: "unsafe",
    short: "Not safe",
    title: "I don't feel safe",
    icon: ShieldAlert,
    tint: "var(--qa-teal-tint)",
    tintStrong: "var(--qa-teal-ink)",
    example:
      "My body feels on alert even if I'm not sure why. I might feel anxious, watchful, tense, frozen, or like I need to escape or protect myself.",
    feels: [
      "Scanning the room, the door, or other people's faces",
      "Startling easily at sounds or movement",
      "Muscles held tight, jaw clenched, shallow breathing",
      "An urge to leave, to hide, or to stay very still",
    ],
    why: "The part of the nervous system that watches for danger works faster than conscious thought, and it can stay switched on after experiences where being alert was necessary. A body on alert without an obvious reason is doing an old job, not making things up.",
    helps:
      "Checking gently whether there is danger right now, and then helping the body notice where it actually is, can begin to lower the alert. Moving somewhere that feels safer, or being near someone you trust, may help more than any exercise.",
    reminders: ["Look around: am I in immediate danger right now?"],
    actions: [
      "Orient yourself to where you are",
      "Move to a place where you feel safer if possible",
      "Contact someone you trust",
    ],
    ctas: [
      { label: "Ground Me Now", to: "/grounding" },
      { label: "Crisis Support", to: "/crisis" },
    ],
  },
];

// ────────────────────────────────────────────────────────────────────
// Wheel geometry
// ────────────────────────────────────────────────────────────────────

const SIZE = 320;
const C = SIZE / 2;
const R_OUTER = 152;
const R_INNER = 76;
const GAP_DEG = 2.4;
const SEG = 360 / STATES.length;

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)] as const;
}

/** Donut-slice path for one segment. */
function segmentPath(index: number, rOuter: number, rInner: number) {
  const start = index * SEG + GAP_DEG / 2;
  const end = (index + 1) * SEG - GAP_DEG / 2;
  const [x1, y1] = polar(C, C, rOuter, start);
  const [x2, y2] = polar(C, C, rOuter, end);
  const [x3, y3] = polar(C, C, rInner, end);
  const [x4, y4] = polar(C, C, rInner, start);
  const large = end - start > 180 ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

function RegulationCompassPage() {
  const [selected, setSelected] = useState<CompassState | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);

  // Bring the details card into view after a choice, gently — never a jump
  // from under the member's finger on a small screen.
  useEffect(() => {
    if (!selected || !detailRef.current) return;
    detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selected]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
      <header className="text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">
          <Compass className="h-3.5 w-3.5" /> Regulation tool
        </div>
        <h1 className="font-display text-2xl tracking-tight text-foreground sm:text-4xl">
          My Regulation Compass
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-[13px] leading-relaxed text-muted-foreground sm:text-base">
          When emotions feel intense, it can be difficult to know what you need.
          Choose what feels closest to your experience right now. Your compass
          will give you a few gentle options to help you regulate and decide
          what to do next.
        </p>
      </header>

      {/* Wheel */}
      <div className="mt-7 flex flex-col items-center">
        <div className="relative w-full max-w-[360px]">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-6 rounded-full bg-gold/15 blur-2xl"
          />
          <svg
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            className="relative w-full select-none"
            role="group"
            aria-label="Regulation compass — choose the state closest to your experience"
          >
            {/* Radiating outer rings */}
            <circle
              cx={C}
              cy={C}
              r={R_OUTER + 6}
              className="fill-none stroke-gold/25"
              strokeWidth={1}
            />
            <circle
              cx={C}
              cy={C}
              r={R_OUTER + 13}
              className="fill-none stroke-gold/15"
              strokeWidth={1}
            />

            {STATES.map((s, i) => {
              const isSelected = selected?.id === s.id;
              const dimmed = selected != null && !isSelected;
              const mid = i * SEG + SEG / 2;
              const [lx, ly] = polar(C, C, (R_OUTER + R_INNER) / 2, mid);
              const [ix, iy] = polar(C, C, (R_OUTER + R_INNER) / 2 - 22, mid);
              const words = s.short.split(" ");
              return (
                <g
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-label={s.title}
                  onClick={() => setSelected(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(s);
                    }
                  }}
                  className="cursor-pointer outline-none [&:focus-visible>path]:stroke-[var(--gold-deep)]"
                >
                  <path
                    d={segmentPath(
                      i,
                      isSelected ? R_OUTER + 4 : R_OUTER,
                      R_INNER,
                    )}
                    fill={`color-mix(in oklab, ${s.tintStrong} 30%, ${s.tint})`}
                    stroke={isSelected ? s.tintStrong : "transparent"}
                    strokeWidth={isSelected ? 2 : 1}
                    style={{ opacity: dimmed ? 0.4 : 1 }}
                    className="transition-all duration-300"
                  />
                  <text
                    x={lx}
                    y={ly + (words.length > 1 ? -1 : 3.5)}
                    textAnchor="middle"
                    style={{ opacity: dimmed ? 0.5 : 1 }}
                    className="pointer-events-none fill-foreground font-medium transition-opacity duration-300"
                    fontSize={9}
                  >
                    {words.length > 1 ? (
                      words.map((w, wi) => (
                        <tspan key={w} x={lx} dy={wi === 0 ? 0 : 11.5}>
                          {w}
                        </tspan>
                      ))
                    ) : (
                      words[0]
                    )}
                  </text>
                </g>
              );
            })}

            {/* Centre */}
            <circle
              cx={C}
              cy={C}
              r={R_INNER - 6}
              className="fill-card stroke-gold/40"
              strokeWidth={1.5}
            />
            <text
              x={C}
              y={C - 12}
              textAnchor="middle"
              className="fill-foreground font-semibold uppercase"
              fontSize={11}
              letterSpacing="0.06em"
            >
              <tspan x={C} dy={0}>
                WHAT DO I NEED
              </tspan>
              <tspan x={C} dy={15}>
                RIGHT NOW?
              </tspan>
            </text>
            <text
              x={C}
              y={C + 30}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize={9}
            >
              {selected ? "Tap another, or start again" : "Choose one to begin"}
            </text>
          </svg>
        </div>

        {/* Tap-friendly list — the wheel is beautiful, this makes every
            state reachable with a comfortable target on small screens. */}
        <div className="mt-5 grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
          {STATES.map((s) => {
            const isSelected = selected?.id === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelected(s)}
                aria-pressed={isSelected}
                className={`flex min-h-12 items-center gap-3 rounded-2xl border px-3.5 py-2.5 text-start transition-all ${
                  isSelected
                    ? "border-[var(--gold-deep)]/60 bg-[var(--gradient-gold)] shadow-sm"
                    : "border-border/70 bg-card hover:border-gold/40"
                }`}
              >
                <span
                  aria-hidden
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full"
                  style={{ backgroundColor: s.tint }}
                >
                  <s.icon
                    className="h-4 w-4"
                    style={{ color: s.tintStrong }}
                    strokeWidth={2}
                  />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium leading-snug text-foreground">
                    {s.title}
                  </span>
                  <span className="mt-1 block text-[11.5px] leading-snug text-muted-foreground line-clamp-2">
                    {s.example}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Details */}
      <div ref={detailRef} className="scroll-mt-24">
        {selected ? (
          <article className="mt-6 overflow-hidden rounded-3xl border border-gold/30 bg-card shadow-sm">
            <div
              className="flex items-center gap-3 px-5 py-4"
              style={{ backgroundColor: selected.tint }}
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-background/80">
                <selected.icon
                  className="h-5 w-5"
                  style={{ color: selected.tintStrong }}
                  strokeWidth={2}
                />
              </span>
              <h2 className="font-display text-lg leading-tight text-foreground sm:text-xl">
                {selected.title}
              </h2>
            </div>

            <div className="space-y-5 px-5 py-5">
              <p className="rounded-2xl border border-border/60 bg-background/60 px-3.5 py-3 text-[13px] italic leading-relaxed text-foreground sm:text-sm">
                &ldquo;{selected.example}&rdquo;
              </p>

              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                  What this can feel like
                </h3>
                <ul className="mt-2 space-y-1.5">
                  {selected.feels.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2.5 text-[13px] leading-relaxed text-muted-foreground sm:text-sm"
                    >
                      <span
                        aria-hidden
                        className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: selected.tintStrong }}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                  Why your nervous system may respond this way
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                  {selected.why}
                </p>
              </section>

              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                  What might help right now
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                  {selected.helps}
                </p>
              </section>

              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                  Gentle reminders
                </h3>
                <ul className="mt-2 space-y-2">
                  {selected.reminders.map((r) => (
                    <li
                      key={r}
                      className="rounded-2xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-[13px] leading-relaxed text-foreground sm:text-sm"
                    >
                      {r}
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                  You might try
                </h3>
                <ul className="mt-2 space-y-1.5">
                  {selected.actions.map((a) => (
                    <li
                      key={a}
                      className="flex items-start gap-2.5 text-[13px] leading-relaxed text-muted-foreground sm:text-sm"
                    >
                      <span
                        aria-hidden
                        className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: selected.tintStrong }}
                      />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
                Tools on Recovery Trauma&trade; you might open
              </h3>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {selected.ctas.map((cta) => (
                  <Link
                    key={cta.to + cta.label}
                    to={cta.to}
                    className="inline-flex min-h-11 flex-1 items-center justify-between gap-2 rounded-full border border-[var(--gold-deep)]/40 bg-[var(--gradient-gold)] px-4 py-2.5 text-[13px] font-medium text-foreground transition-all hover:shadow-md sm:text-sm"
                  >
                    {cta.label}
                    <ArrowRight className="h-4 w-4 shrink-0" />
                  </Link>
                ))}
              </div>

              {selected.id === "unsafe" && (
                <p className="flex items-start gap-2 rounded-2xl border border-border/60 bg-background/60 px-3.5 py-3 text-[12px] leading-relaxed text-muted-foreground">
                  <LifeBuoy className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
                  <span>
                    If there is immediate danger or a crisis, please use the{" "}
                    <Link to="/crisis" className="underline underline-offset-2">
                      Crisis page
                    </Link>{" "}
                    or your local emergency services.
                  </span>
                </p>
              )}

              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setSelected(null);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-[13px] font-medium text-foreground transition-colors hover:border-gold/50"
                >
                  <RotateCcw className="h-4 w-4" /> Start Again
                </button>
              </div>
            </div>
          </article>
        ) : null}
      </div>

      <p className="mx-auto mt-6 max-w-xl rounded-2xl border border-lavender/30 bg-lavender/10 px-4 py-3 text-center text-[12px] leading-relaxed text-muted-foreground">
        You don't need to do everything suggested. Choose one thing that feels
        manageable right now.
      </p>

      <p className="mx-auto mt-3 max-w-xl text-center text-[11px] leading-relaxed text-muted-foreground/80">
        These experiences can have many different causes, including things that
        have nothing to do with trauma. My Regulation Compass is a
        self-reflection and self-regulation tool, not a diagnosis or an
        assessment, and no technique is promised to work for everyone — you know
        your own experience best.
      </p>
    </div>
  );
}
